import { FormComponentPropLanguages } from "components/Form/components/FormComponentProps";

/**
 * How a FILE record's content should be rendered on the Console, derived from the
 * server-detected `content_type` (see backend resources/file.py
 * `guess_file_content_type`). Drives SwitchContentFormComponent.
 * (operator-task-fe-file-mimetype-adaptive-display.)
 *
 * - "text"     -> plaintext code editor (current behaviour; also the fallback for
 *                 legacy files whose content_type has not been back-filled yet).
 * - "code"     -> code editor with a syntax-highlighting language (`lang`).
 * - "image"    -> inline, size-capped <img> preview.
 * - "download" -> no inline render; show a download hint only (binary / pdf /
 *                 audio / video / archives / untrusted svg).
 */
export type FileDisplayCategory = "text" | "code" | "image" | "download";

export interface FileDisplay {
    category: FileDisplayCategory;
    lang: FormComponentPropLanguages;
}

/**
 * Map a server-detected MIME content_type to a Monaco language id understood by
 * the light editor profile (see monacoSetup.ts `loadLightMonaco`). Types that are
 * texty but have no dedicated highlight grammar map to "text" (plaintext editor).
 */
const CONTENT_TYPE_TO_LANG: Record<string, FormComponentPropLanguages> = {
    "text/x-python": "python",
    "application/x-python": "python",
    "application/json": "json",
    "application/yaml": "yaml",
    "text/yaml": "yaml",
    "application/x-yaml": "yaml",
    "text/javascript": "javascript",
    "application/javascript": "javascript",
    "text/typescript": "typescript",
    "application/typescript": "typescript",
    "application/sql": "sql",
    "text/x-sql": "sql",
    "application/xml": "xml",
    "text/xml": "xml",
    "text/html": "html",
    "text/markdown": "markdown",
    "application/x-sh": "shell",
    "text/x-shellscript": "shell",
    "text/x-dockerfile": "dockerfile",
    "text/x-ini": "ini",
    // texty types without a dedicated grammar -> plaintext editor
    "application/toml": "text",
    "text/csv": "text",
    "text/x-makefile": "text",
    "text/plain": "text",
};

/** Non-text types that must never be dumped into the editor: download hint only. */
const DOWNLOAD_ONLY_TYPES: ReadonlySet<string> = new Set<string>([
    "application/octet-stream",
    "application/pdf",
    // archives
    "application/zip",
    "application/gzip",
    "application/x-gzip",
    "application/x-tar",
    "application/x-7z-compressed",
    "application/x-rar-compressed",
    "application/x-bzip2",
]);

export const resolveFileDisplay = (contentType?: string | null): FileDisplay => {
    const ct = (contentType ?? "").toLowerCase().split(";", 1)[0].trim();
    if (!ct) {
        // legacy files (content_type not yet back-filled) -> current plaintext behaviour
        return { category: "text", lang: "text" };
    }
    if (ct === "image/svg+xml") {
        // SVG can embed scripts -> download-only in v1 (no inline render, XSS risk)
        return { category: "download", lang: "text" };
    }
    if (ct.startsWith("image/")) {
        return { category: "image", lang: "text" };
    }
    if (DOWNLOAD_ONLY_TYPES.has(ct) || ct.startsWith("audio/") || ct.startsWith("video/")) {
        return { category: "download", lang: "text" };
    }
    const lang = CONTENT_TYPE_TO_LANG[ct];
    if (lang) {
        return { category: lang === "text" ? "text" : "code", lang };
    }
    if (ct.startsWith("text/")) {
        // any other text/* -> plaintext editor
        return { category: "text", lang: "text" };
    }
    // Unknown application/* etc. The backend only emits a non-text type here when
    // the content did NOT look like text, so treating it as binary is safe.
    return { category: "download", lang: "text" };
};
